/* ============================================================
   delivery.js: free delivery progress bars and the dispatch message.
   Display only. What checkout charges is set in Settings > Shipping and
   delivery and the SMART Shipping Rates & Rules app; see the comment at
   the top of snippets/free-delivery.liquid.

   - Bars ([data-fd-bar], snippets/free-delivery.liquid) and the heavy
     order notice ([data-fd-heavy]) are painted by Liquid, then updated here
     from /cart.js after every cart change: cart.js calls
     FAWPDelivery.update(cart) when it already has the cart, and a
     PerformanceObserver catches any other /cart/add, /cart/change,
     /cart/update or /cart/clear request (apps, the pump finder) and
     refetches /cart.js.
   - Dispatch wording (assets/dispatch.js works it out, in UK time):
     the product page line and the basket line ([data-fd-dispatch],
     snippets/dispatch-message), the trust strip column ([data-fd-usp]) and
     the out-of-hours version of the product page help card
     ([data-help-hours]). Non-dispatch dates and the cutoff come from Theme
     settings > Free delivery & dispatch (window.theme.delivery).
   ============================================================ */
(function () {
  'use strict';

  var cfg = (window.theme && window.theme.delivery) || {};

  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---------- Dispatch wording ---------- */

  var closedDates = null;
  function dispatchMessages() {
    var D = window.FAWPDispatch;
    if (!D) return null;
    if (!closedDates) closedDates = D.parseClosed(cfg.closedDates);
    return D.messages(D.state(new Date(), { cutoffHour: cfg.cutoffHour, closed: closedDates }));
  }

  /* A basket's line: 'ok' when every line can go out today (lines carry
     data-dispatch-ok from snippets/line-dispatch-note), 'mixed' when one
     can't (lead time or out of stock), 'none' for a quoted (heavy) basket,
     which can't check out. */
  function cartDispatchMode(el) {
    var scope = el.closest('[data-fd-cart]');
    if (!scope) return 'ok';
    var heavy = scope.querySelector('[data-fd-heavy]');
    if (heavy && !heavy.hidden) return 'none';
    var lines = scope.querySelectorAll('[data-cart-line]');
    if (!lines.length) return 'none';
    return scope.querySelector('[data-cart-line][data-dispatch-ok="0"]') ? 'mixed' : 'ok';
  }

  /* Never throws: cart.js calls this inside its add-to-basket chain, and a
     failure there would resubmit the form. The Liquid text stays on error. */
  function updateDispatch() {
    try {
      var m = dispatchMessages();
      if (!m) return;

      $$('[data-fd-dispatch]').forEach(function (el) {
        var text = el.querySelector('[data-fd-dispatch-text]');
        if (el.hasAttribute('data-fd-dispatch-scope')) {
          var mode = cartDispatchMode(el);
          el.hidden = mode === 'none';
          el.setAttribute('data-fd-dispatch-mode', mode);
          if (text) text.innerHTML = mode === 'mixed' ? m.cartMixed : m.cart;
        } else if (text) {
          text.innerHTML = m.product;
        }
      });

      $$('[data-fd-usp]').forEach(function (el) { el.textContent = m.usp; });

      /* The card's "dispatched tomorrow" sentence only where the product's
         own dispatch line shows: not on lead-time products (no line) or an
         out of stock variant (line hidden). */
      var pdpLine = document.querySelector('[data-fd-dispatch][data-delivery-countdown]');
      var pdpShipsNow = !!pdpLine && !pdpLine.hidden;
      $$('[data-help-hours]').forEach(function (card) {
        card.setAttribute('data-help-state', m.officeOpen ? 'open' : 'closed');
        var d = card.querySelector('[data-help-dispatch]');
        var r = card.querySelector('[data-help-reply]');
        if (d) { d.textContent = m.helpDispatch; d.hidden = !pdpShipsNow; }
        if (r) r.textContent = m.helpReply;
      });
    } catch (e) { /* keep the fallback wording */ }
  }

  /* ---------- Free delivery bars ---------- */

  /* Shop money format, without pence when the amount is whole pounds
     (the same as Liquid's money_without_trailing_zeros). */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '£{{amount}}';
    var amount = (Math.max(0, cents) / 100).toFixed(2).replace(/\.00$/, '');
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*\w+\s*\}\}/, parts.join('.'));
  }

  var lastUpdate = 0;

  function update(cart) {
    try { render(cart); } catch (e) { /* display only: never break the caller */ }
  }

  function render(cart) {
    if (!cart || typeof cart.items_subtotal_price === 'undefined') return;
    lastUpdate = Date.now();
    var subtotal = cart.items_subtotal_price;
    var weight = cart.total_weight || 0;

    $$('[data-fd-bar]').forEach(function (bar) {
      var threshold = parseInt(bar.getAttribute('data-fd-threshold'), 10) || 0;
      var maxGrams = parseInt(bar.getAttribute('data-fd-max-grams'), 10) || 0;
      var heavyGrams = parseInt(bar.getAttribute('data-fd-heavy-grams'), 10) || 0;
      /* Same order as snippets/free-delivery.liquid: over the weight limit
         can't qualify, whatever the value. 0 means no weight limit. */
      var state;
      if (cart.item_count === 0) state = 'empty';
      else if (maxGrams > 0 && weight > maxGrams) state = 'overweight';
      else if (subtotal < threshold) state = 'below';
      else state = 'qualified';

      bar.hidden = heavyGrams > 0 && weight > heavyGrams;
      bar.setAttribute('data-fd-state', state);
      $$('[data-fd-msg]', bar).forEach(function (m) { m.hidden = m.getAttribute('data-fd-msg') !== state; });
      $$('[data-fd-msg-extra]', bar).forEach(function (m) { m.hidden = m.getAttribute('data-fd-msg-extra') !== state; });
      var remaining = bar.querySelector('[data-fd-remaining]');
      if (remaining) remaining.textContent = formatMoney(threshold - subtotal);

      var pct = 0;
      if (state === 'below' && threshold > 0) pct = Math.floor(subtotal * 100 / threshold);
      else if (state !== 'empty') pct = 100;
      var track = bar.querySelector('[role="progressbar"]');
      if (track) {
        track.hidden = state === 'empty';
        track.setAttribute('aria-valuenow', pct);
        var fill = track.firstElementChild;
        if (fill) fill.style.width = pct + '%';
      }
    });

    $$('[data-fd-heavy]').forEach(function (note) {
      var grams = parseInt(note.getAttribute('data-fd-heavy-grams'), 10) || 0;
      note.hidden = !(grams > 0 && weight > grams);
    });

    /* Drawer "Delivery from £X + VAT": the price for the basket weight, from
       the bands in snippets/delivery-rates.liquid (data-fd-rates). */
    $$('[data-fd-basket-price]').forEach(function (el) {
      var r;
      try { r = JSON.parse(el.getAttribute('data-fd-rates')); } catch (e) { return; }
      if (!r || !r.bands || !r.bands.length) return;
      var pence = r.bands[r.bands.length - 1][1];
      if (weight <= 0) pence = r.bands[0][1];
      else {
        for (var i = 0; i < r.bands.length; i++) {
          if (weight <= r.bands[i][0]) { pence = r.bands[i][1]; break; }
        }
      }
      var fm = window.theme && window.theme.formatMoney;
      el.textContent = fm ? fm(pence).replace(/[.,]00(?!\d)/, '') : '£' + (pence / 100);
    });

    /* Drawer delivery sentence: quoted, free, or the standard wording. */
    $$('[data-fd-cart]').forEach(function (scope) {
      var notes = scope.querySelectorAll('[data-fd-note]');
      if (!notes.length) return;
      var heavy = scope.querySelector('[data-fd-heavy]');
      var bar = scope.querySelector('[data-fd-bar]');
      var noteState = 'other';
      if (heavy && !heavy.hidden) noteState = 'heavy';
      else if (bar && !bar.hidden && bar.getAttribute('data-fd-state') === 'qualified') noteState = 'qualified';
      Array.prototype.forEach.call(notes, function (n) { n.hidden = n.getAttribute('data-fd-note') !== noteState; });
    });

    /* Product page: hide "UK delivery from £X + VAT" while the basket
       qualifies for free delivery (the bar says so right above it). */
    var pdpBar = document.querySelector('.bb-fd [data-fd-bar]');
    $$('[data-fd-std-delivery]').forEach(function (li) {
      li.hidden = !!pdpBar && !pdpBar.hidden && pdpBar.getAttribute('data-fd-state') === 'qualified';
    });

    updateDispatch();
  }

  /* Refetch the cart (debounced) after a cart change we weren't handed. */
  var refreshTimer = null;
  function refresh() {
    if (!document.querySelector('[data-fd-bar], [data-fd-heavy], [data-fd-dispatch-scope]')) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      /* cart.js has just handed us this change: no need to fetch it again. */
      if (Date.now() - lastUpdate < 1000) return;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(update)
        .catch(function () { /* keep the last state */ });
    }, 150);
  }

  function watchCartRequests() {
    if (!('PerformanceObserver' in window)) return;
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(entry.name)) refresh();
        });
      }).observe({ type: 'resource' });
    } catch (e) { /* older browsers: cart.js still calls update() directly */ }
  }

  function init() {
    updateDispatch();
    /* Every minute, on the minute (the countdown shows minutes). */
    setTimeout(function () { updateDispatch(); setInterval(updateDispatch, 60000); }, 60500 - (Date.now() % 60000));
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') updateDispatch();
    });
    watchCartRequests();
    /* Variant change: product.js shows or hides the dispatch line in its own
       handler; re-run once it has. */
    var picker = document.querySelector('[data-variant-picker]');
    if (picker) {
      picker.addEventListener('click', function () { setTimeout(updateDispatch, 50); });
      picker.addEventListener('change', function () { setTimeout(updateDispatch, 50); });
    }
    /* Back/forward cache: the basket may have changed in another tab. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  }

  window.FAWPDelivery = { update: update, refresh: refresh, updateDispatch: updateDispatch };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
